import { AnyAction, MiddlewareAPI } from 'redux';
import { LayoutAction, LayoutActionType } from 'layout/store/actions';
import { ThunkDispatch } from 'store/store';
import { LayoutStatus } from 'layout/LayoutState';
import Services from 'store/Services';
import { v4 as uuid } from 'uuid';
import { LayoutLoadedMessage } from 'embedded/api/out/messages/LayoutLoadedMessage';
import {
    actionAffectsAuthenticationStatus,
    LoginStateChangeAction,
} from 'login/store/actions';
import { AuthenticationStatusChangedMessage } from 'embedded/api/out/messages/AuthenticationStatusChangedMessage';
import { selectLoginState } from 'login/store/selectors';
import State from 'store/State';
import { LoginStatus } from 'login/store/LoginState';

export const embeddedApiMiddleware = (services: Services) => [
    sendLayoutLoadedMessageMiddleware(services),
    sendAuthenticationStatusChangeMessageMiddleware(services),
    processPendingAuthenticationMessageWhenBecomesAuthenticated(services),
];

export function sendLayoutLoadedMessageMiddleware(services: Services) {
    const { embeddedApiService } = services;

    return () => (next: ThunkDispatch<AnyAction>) => (action: LayoutAction) => {
        next(action);

        if (action.type !== LayoutActionType.LAYOUT_STATE_CHANGE) {
            return;
        }

        const layoutState = action.payload!.layoutState;
        if (layoutState.status !== LayoutStatus.SUCCESS) {
            return;
        }

        if (!layoutState.result) {
            return;
        }

        const message: LayoutLoadedMessage = {
            topic: 'LAYOUT_LOADED',
            payload: {
                correlationId: uuid(),
                layoutName: layoutState.result!.name,
            },
        };
        embeddedApiService.send(message);
    };
}

export function sendAuthenticationStatusChangeMessageMiddleware(
    services: Services
) {
    const { embeddedApiService } = services;

    return (middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: LoginStateChangeAction) => {
            next(action);

            if (!actionAffectsAuthenticationStatus(action)) {
                return;
            }

            const status = selectLoginState(middlewareAPI.getState()).status;

            const message: AuthenticationStatusChangedMessage = {
                topic: 'AUTHENTICATION_STATUS_CHANGED',
                payload: {
                    correlationId: uuid(),
                    status,
                },
            };
            embeddedApiService.send(message);
        };
}

export function processPendingAuthenticationMessageWhenBecomesAuthenticated(
    services: Services
) {
    const { embeddedApiService } = services;

    return (middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: LoginStateChangeAction) => {
            next(action);

            if (!actionAffectsAuthenticationStatus(action)) {
                return;
            }

            const status = selectLoginState(middlewareAPI.getState()).status;

            if (status !== LoginStatus.SUCCESS) {
                return;
            }

            embeddedApiService.processPendingAuthenticationMessages();
        };
}
